/**
 * Builder main gallery: prev/next buttons off — arrow cursors on left/right halves;
 * tap/click without dragging goes previous / next (Flickity swipe unchanged).
 * Hit target is [data-media-viewer] (full aspect-ratio main media box), not only .flickity-viewport / the bitmap.
 */
(function () {
  "use strict";

  const COARSE =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;

  function slideCount(flk) {
    try {
      if (flk.slides && flk.slides.length) return flk.slides.length;
      if (flk.cells && flk.cells.length) return flk.cells.length;
    } catch (_) {}
    return 0;
  }

  function isMobileViewport() {
    return (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 768px)").matches
    );
  }

  function prefersReducedMotion() {
    return (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  // Flickity default is 3px sideways — page scroll over the sticky photos
  // then steals into a swipe. Mobile needs a clearly horizontal flick.
  const MOBILE_SWIPE_MIN_X = 36;
  const MOBILE_SWIPE_AXIS_RATIO = 1.4;

  function hasMobileSwipeIntent(moveVector) {
    if (!moveVector) return false;
    const ax = Math.abs(Number(moveVector.x) || 0);
    const ay = Math.abs(Number(moveVector.y) || 0);
    return ax >= MOBILE_SWIPE_MIN_X && ax > ay * MOBILE_SWIPE_AXIS_RATIO;
  }

  function bindSwipeIntent(flk) {
    if (!flk || flk._wbSwipeIntentBound) return;
    if (typeof flk.hasDragStarted !== "function") return;
    flk._wbSwipeIntentBound = true;

    const originalHasDragStarted = flk.hasDragStarted.bind(flk);
    flk.hasDragStarted = function (moveVector) {
      if (isMobileViewport()) {
        return hasMobileSwipeIntent(moveVector);
      }
      return originalHasDragStarted(moveVector);
    };

    if (flk.options && isMobileViewport()) {
      const current = Number(flk.options.dragThreshold);
      if (!Number.isFinite(current) || current < MOBILE_SWIPE_MIN_X) {
        flk.options.dragThreshold = MOBILE_SWIPE_MIN_X;
      }
    }
  }

  function syncPagination(pagination, selectedIndex) {
    if (!pagination) return;
    let activeDot = null;
    pagination.querySelectorAll("[data-wb-gallery-dot]").forEach(function (dot) {
      const active = Number(dot.dataset.wbGalleryDot) === Number(selectedIndex);
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-current", active ? "true" : "false");
      if (active) activeDot = dot;
    });

    if (
      !activeDot ||
      typeof activeDot.getBoundingClientRect !== "function" ||
      typeof activeDot.scrollIntoView !== "function" ||
      typeof pagination.getBoundingClientRect !== "function"
    ) {
      return;
    }

    const dotRect = activeDot.getBoundingClientRect();
    const railRect = pagination.getBoundingClientRect();
    if (dotRect.left < railRect.left || dotRect.right > railRect.right) {
      activeDot.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }

  function bindPagination(gallery, flk) {
    const pagination = gallery.querySelector("[data-wb-gallery-pagination]");
    if (!pagination || !flk) return;

    if (pagination._wbFlickity === flk) {
      syncPagination(pagination, flk.selectedIndex || 0);
      return;
    }

    if (
      pagination._wbFlickity &&
      pagination._wbChangeHandler &&
      typeof pagination._wbFlickity.off === "function"
    ) {
      pagination._wbFlickity.off("change", pagination._wbChangeHandler);
    }

    if (!pagination._wbClickBound) {
      pagination.addEventListener("click", function (event) {
        const dot = event.target.closest("[data-wb-gallery-dot]");
        if (!dot) return;
        if (typeof pagination.contains === "function" && !pagination.contains(dot)) return;
        const index = Number(dot.dataset.wbGalleryDot);
        if (!Number.isInteger(index)) return;
        event.preventDefault();
        event.stopPropagation();
        pagination._wbFlickity?.select(index, false, false);
      });
      pagination._wbClickBound = true;
    }

    const handleChange = function (index) {
      syncPagination(pagination, index);
    };

    pagination._wbFlickity = flk;
    pagination._wbChangeHandler = handleChange;
    flk.on("change", handleChange);
    syncPagination(pagination, flk.selectedIndex || 0);
  }

  function unbindHalfNav(viewport) {
    const binding = viewport && viewport._wbHalfNavBinding;
    if (!binding) return;

    viewport.removeEventListener("pointerdown", binding.pointerdown, true);
    viewport.removeEventListener("click", binding.click, true);
    if (binding.mousemove) {
      viewport.removeEventListener("mousemove", binding.mousemove);
      viewport.removeEventListener("mouseleave", binding.mouseleave);
    }
    viewport.classList.remove("wb-gallery-cursor--prev", "wb-gallery-cursor--next");
    delete viewport._wbHalfNavBinding;
    delete viewport.dataset.wbHalfNavBound;
  }

  function bindHalfNav(viewport, flk) {
    if (!viewport || !flk) return;
    if (viewport._wbHalfNavBinding?.flickity === flk) return;
    unbindHalfNav(viewport);
    viewport.dataset.wbHalfNavBound = "1";

    function updateCursor(clientX) {
      if (isMobileViewport()) {
        clearCursor();
        return;
      }
      if (COARSE) return;
      const n = slideCount(flk);
      if (n <= 1) {
        viewport.classList.remove("wb-gallery-cursor--prev", "wb-gallery-cursor--next");
        return;
      }
      const rect = viewport.getBoundingClientRect();
      const x = clientX - rect.left;
      const left = x < rect.width * 0.5;
      viewport.classList.toggle("wb-gallery-cursor--prev", left);
      viewport.classList.toggle("wb-gallery-cursor--next", !left);
    }

    function clearCursor() {
      viewport.classList.remove("wb-gallery-cursor--prev", "wb-gallery-cursor--next");
    }

    function shouldIgnoreTarget(target) {
      if (!target || !target.closest) return false;
      return !!target.closest(
        "button, a[href], input, textarea, select, [role='button'], video, model-viewer, .shopify-model-viewer-ui, .f-video__play, [data-shopify-xr]"
      );
    }

    function goFromClientX(clientX) {
      if (slideCount(flk) <= 1) return;
      const rect = viewport.getBoundingClientRect();
      const x = clientX - rect.left;
      if (x < rect.width * 0.5) flk.previous();
      else flk.next();
    }

    let downX;
    let downY;
    let downT;

    const pointerdown = function (e) {
      if (isMobileViewport()) {
        downX = null;
        downY = null;
        downT = null;
        return;
      }
      if (e.pointerType === "mouse" && e.button !== 0) return;
      downX = e.clientX;
      downY = e.clientY;
      downT = Date.now();
    };

    const click = function (e) {
      if (isMobileViewport()) {
        downX = null;
        downY = null;
        downT = null;
        return;
      }
      if (shouldIgnoreTarget(e.target)) return;
      if (slideCount(flk) <= 1) return;
      if (downX == null || downY == null) return;
      const dist = Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY);
      const dt = Date.now() - (downT || 0);
      downX = null;
      downY = null;
      if (dist > 16) return;
      if (dt > 900) return;
      goFromClientX(e.clientX);
    };

    let mousemove = null;
    let mouseleave = null;
    if (!COARSE && !prefersReducedMotion()) {
      mousemove = function (e) {
        updateCursor(e.clientX);
      };
      mouseleave = clearCursor;
      viewport.addEventListener("mousemove", mousemove, { passive: true });
      viewport.addEventListener("mouseleave", mouseleave, { passive: true });
    }

    viewport.addEventListener("pointerdown", pointerdown, true);
    viewport.addEventListener("click", click, true);
    viewport._wbHalfNavBinding = {
      flickity: flk,
      pointerdown,
      click,
      mousemove,
      mouseleave,
    };
  }

  function tryInitFlickity(fc, hitZone, gallery) {
    const flk = fc.slider && fc.slider.instance;
    if (!flk || !hitZone) return false;
    bindPagination(gallery, flk);
    bindHalfNav(hitZone, flk);
    bindSwipeIntent(flk);
    return true;
  }

  function initGallery(gallery) {
    if (gallery._wbGalleryLifecycle) {
      gallery._wbGalleryLifecycle.rebind();
      return;
    }

    const rebind = function () {
      const viewer = gallery.querySelector(".f-product__media-viewer--carousel");
      if (!viewer) return false;
      const fc = viewer.querySelector(
        "flickity-component.f-product__media-list.wheelbuilder-gallery-hover-nav"
      );
      if (!fc) return false;
      const hitZone =
        viewer.querySelector("[data-media-viewer]") ||
        fc.querySelector(".flickity-viewport");
      return tryInitFlickity(fc, hitZone, gallery);
    };

    const observer = new MutationObserver(rebind);
    observer.observe(gallery, { childList: true, subtree: true });
    document.addEventListener("matchTablet", rebind);
    document.addEventListener("unmatchTablet", rebind);
    gallery._wbGalleryLifecycle = { observer, rebind };
    rebind();
  }

  function teardownGallery(gallery) {
    const life = gallery && gallery._wbGalleryLifecycle;
    if (!life) return;
    if (life.observer) life.observer.disconnect();
    document.removeEventListener("matchTablet", life.rebind);
    document.removeEventListener("unmatchTablet", life.rebind);
    gallery._wbGalleryLifecycle = null;
  }

  function run() {
    document.querySelectorAll(".wheelbuilder-product-gallery").forEach(initGallery);
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      bindHalfNav,
      bindPagination,
      bindSwipeIntent,
      hasMobileSwipeIntent,
      initGallery,
      syncPagination,
      MOBILE_SWIPE_MIN_X,
      MOBILE_SWIPE_AXIS_RATIO,
    };
  }

  if (typeof document === "undefined") return;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  document.addEventListener("shopify:section:load", run);
  document.addEventListener("shopify:section:unload", function onSectionUnload(ev) {
    const root = ev && ev.target;
    if (!root || typeof root.querySelectorAll !== "function") return;
    root.querySelectorAll(".wheelbuilder-product-gallery").forEach(teardownGallery);
    if (root.classList && root.classList.contains("wheelbuilder-product-gallery")) {
      teardownGallery(root);
    }
  });
})();
